import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowRight, Key, Mail, MessageCircle, PawPrint } from 'lucide-react'

import { auth, ownerAuth } from './api/client'
import { Button } from './components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card'
import { Label } from './components/ui/label'
import { Textarea } from './components/ui/input'

export default function Login() {
  const { t } = useTranslation()
  const nav = useNavigate()
  const [params] = useSearchParams()
  const [token, setToken] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Surface the error reason from a failed magic-link / LINE callback as
  // a translated banner. Keys: state | channel | exchange | browser_mismatch
  // | expired | used | invalid.
  const errorKey = params.get('error')

  function submitJwt() {
    if (!token.trim()) return
    // Audit-log on the backend would require a roundtrip; for the escape
    // hatch we just store + bounce. A console.warn is enough of a paper
    // trail for "I used the dev field".
    // eslint-disable-next-line no-console
    console.warn('[owner-web] using JWT-paste escape hatch')
    auth.set(token)
    nav('/', { replace: true })
  }

  return (
    <div className="container max-w-md py-16">
      <div className="mb-10 flex items-center gap-2 text-base font-semibold">
        <div className="grid size-9 place-items-center rounded-lg bg-primary text-primary-foreground">
          <PawPrint className="size-5" />
        </div>
        <span>{t('brand')}</span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('auth.loginTitle')}</CardTitle>
          <CardDescription>{t('auth.loginSubtitle')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorKey && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {t(`auth.error.${errorKey}`, t('auth.error.generic'))}
            </div>
          )}

          {/* Primary: LINE */}
          <a href={ownerAuth.lineStartUrl()} className="block">
            <Button className="w-full bg-[#06C755] text-white hover:bg-[#05B04C]">
              <MessageCircle className="size-4" />
              {t('auth.continueWithLine')}
            </Button>
          </a>

          {/* Secondary: email */}
          <Button
            variant="outline"
            className="w-full"
            onClick={() => nav('/login/email')}
          >
            <Mail className="size-4" />
            {t('auth.signInWithEmail')}
          </Button>

          <p className="pt-1 text-center text-xs text-muted-foreground/80">
            {t('auth.firstTimeHint')}
          </p>

          {/* Escape hatch: collapsed JWT-paste */}
          <details
            className="rounded-md border border-border bg-secondary/30 px-3 py-2"
            open={showAdvanced}
            onToggle={(e) => setShowAdvanced((e.target as HTMLDetailsElement).open)}
          >
            <summary className="cursor-pointer text-xs font-medium text-muted-foreground">
              <Key className="mr-1 inline size-3" />
              {t('auth.advancedJwt')}
            </summary>
            <div className="mt-3 space-y-2">
              <Label htmlFor="tok" className="text-xs">
                {t('auth.tokenLabel')}
              </Label>
              <Textarea
                id="tok"
                rows={4}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="eyJhbGciOi…"
                className="font-mono text-[11px]"
              />
              <p className="text-[11px] text-muted-foreground/70">
                {t('auth.advancedJwtHint')}
              </p>
              <Button
                size="sm"
                variant="secondary"
                onClick={submitJwt}
                disabled={!token.trim()}
                className="w-full"
              >
                {t('auth.enter')}
                <ArrowRight className="ml-1 size-3.5" />
              </Button>
            </div>
          </details>
        </CardContent>
      </Card>
    </div>
  )
}
